/**
 * PatternRecognizer
 *
 * Post-layout pass over {@link InfraLayoutOutput}. Detects well-known
 * AWS architectural patterns (3-tier web, queue worker, serverless
 * API, etc.) so the canvas can annotate them with a named label and
 * optional compact rendering.
 *
 * Pure function — no layout side effects. Matches are advisory: the
 * materializer chooses how to use them.
 *
 * Patterns ship with signatures expressed as predicates over the
 * layout output, not literal graph isomorphism. This keeps matching
 * tolerant to scanner noise (extra nodes, unused roles) and avoids
 * coupling to specific resource-id shapes.
 */

import type { InfraLayoutOutput } from './InfraLayoutEngine';
import { classifyInfraTier } from './InfraLayoutEngine';

export interface MatchedPattern {
  patternId: string;
  label: string;
  /** Leaf + cluster-rep ids that participate in the pattern. */
  memberIds: string[];
  /** Primary resource — the "hero" of the pattern. Used as render anchor. */
  anchorId: string;
  /** Optional canvas hints. */
  hints?: {
    backgroundColor?: string;
    renderAsCompact?: boolean;
  };
}

export interface PatternRecognizerInput {
  layout: InfraLayoutOutput;
  /** resourceId → manifestId lookup for every shape in the view. */
  manifestByResourceId: Map<string, string>;
  /** All flow edges AFTER cluster rewriting. */
  flowEdges: Array<{ source: string; target: string; kind: string }>;
}

export class PatternRecognizer {
  recognize(input: PatternRecognizerInput): MatchedPattern[] {
    const { layout, manifestByResourceId, flowEdges } = input;

    // Build a tier-indexed view of visible resources (leaves not
    // hidden + cluster reps). A leaf id that is a cluster rep stands
    // for the whole cluster.
    const visibleIds = new Set<string>();
    for (const id of layout.positions.keys()) {
      if (!layout.hiddenMemberIds.has(id)) visibleIds.add(id);
    }

    const byTier = {
      ingress: [] as string[],
      network: [] as string[],
      compute: [] as string[],
      data: [] as string[],
      shared: [] as string[],
      other: [] as string[],
    };
    const manifestByVisibleId = new Map<string, string>();
    for (const id of visibleIds) {
      const m = manifestByResourceId.get(id) ?? '';
      manifestByVisibleId.set(id, m);
      const tier = classifyInfraTier(m);
      byTier[tier].push(id);
    }

    // Index edges by endpoint so each pattern detector can do
    // constant-time neighbour queries.
    const outgoing = new Map<string, Set<string>>();
    const incoming = new Map<string, Set<string>>();
    for (const e of flowEdges) {
      if (!outgoing.has(e.source)) outgoing.set(e.source, new Set());
      outgoing.get(e.source)!.add(e.target);
      if (!incoming.has(e.target)) incoming.set(e.target, new Set());
      incoming.get(e.target)!.add(e.source);
    }

    const matches: MatchedPattern[] = [];

    // First-match-wins for now; priority order matters because a
    // narrow pattern (serverless-api) should win over a generic one
    // (event-pipeline) when both apply.
    matches.push(...this.matchServerlessApi(byTier, manifestByVisibleId, outgoing));
    matches.push(...this.match3TierWeb(byTier, manifestByVisibleId, outgoing, incoming));
    matches.push(...this.matchQueueWorker(byTier, manifestByVisibleId, outgoing, incoming));

    // Dedup: if two patterns claim the same anchor, keep the first one
    // (earlier = more specific per the priority ordering above).
    const seenAnchors = new Set<string>();
    return matches.filter((m) => {
      if (seenAnchors.has(m.anchorId)) return false;
      seenAnchors.add(m.anchorId);
      return true;
    });
  }

  // ── Pattern: Serverless API ──────────────────────────────────
  // APIGw → Lambda → Dynamo/S3
  // Most specific of the lambda-first patterns. Runs before event
  // pipeline so a 3-hop serverless API doesn't get double-labelled.
  private matchServerlessApi(
    byTier: Record<string, string[]>,
    manifest: Map<string, string>,
    outgoing: Map<string, Set<string>>,
  ): MatchedPattern[] {
    const out: MatchedPattern[] = [];
    const apiGws = byTier.ingress.filter((id) => {
      const m = manifest.get(id) ?? '';
      return m === 'aws_api_gateway' || m === 'aws_apigateway' || m === 'aws_apigatewayv2';
    });
    for (const apiGw of apiGws) {
      const fn = [...(outgoing.get(apiGw) ?? [])]
        .find((t) => manifest.get(t) === 'aws_lambda' || manifest.get(t) === 'aws_lambda_function');
      if (!fn) continue;
      const store = [...(outgoing.get(fn) ?? [])].find((t) => {
        const m = manifest.get(t) ?? '';
        return m === 'aws_dynamodb' || m === 'aws_dynamodb_table' || m === 'aws_s3' || m === 'aws_s3_bucket';
      });
      if (!store) continue;
      out.push({
        patternId: 'serverless-api',
        label: 'Serverless API',
        memberIds: [apiGw, fn, store],
        anchorId: fn,
        hints: { backgroundColor: '#f0fdf4' },
      });
    }
    return out;
  }

  // ── Pattern: 3-tier web ──────────────────────────────────────
  // ALB/NLB → ASG/ECS compute → RDS/Aurora
  // Accepts any compute cluster rep between LB and DB, as long as
  // the compute node sits on a path from LB to a data-tier node.
  private match3TierWeb(
    byTier: Record<string, string[]>,
    manifest: Map<string, string>,
    outgoing: Map<string, Set<string>>,
    incoming: Map<string, Set<string>>,
  ): MatchedPattern[] {
    const out: MatchedPattern[] = [];
    const lbs = byTier.ingress.filter((id) => {
      const m = manifest.get(id) ?? '';
      return m === 'aws_alb' || m === 'aws_nlb' || m === 'aws_elb' || m === 'aws_clb';
    });
    const dbs = new Set(
      byTier.data.filter((id) => {
        const m = manifest.get(id) ?? '';
        return m === 'aws_rds' || m === 'aws_rds_instance' || m === 'aws_rds_cluster' || m === 'aws_aurora';
      }),
    );
    for (const lb of lbs) {
      const compute = [...(outgoing.get(lb) ?? [])].find(
        (t) => classifyInfraTier(manifest.get(t) ?? '') === 'compute',
      );
      if (!compute) continue;
      const db = [...(outgoing.get(compute) ?? [])].find((t) => dbs.has(t));
      if (!db) continue;
      out.push({
        patternId: '3tier-web',
        label: 'Web tier',
        memberIds: [lb, compute, db],
        anchorId: compute,
        hints: { backgroundColor: '#eff6ff' },
      });
    }
    void incoming;
    return out;
  }

  // ── Pattern: Queue worker ────────────────────────────────────
  // SQS queue ← Compute cluster that reads from the queue.
  // We detect via incoming edges to the queue whose sources are
  // compute-tier. Queue is the "fan-in" anchor.
  private matchQueueWorker(
    byTier: Record<string, string[]>,
    manifest: Map<string, string>,
    outgoing: Map<string, Set<string>>,
    incoming: Map<string, Set<string>>,
  ): MatchedPattern[] {
    const out: MatchedPattern[] = [];
    const queues = [...byTier.shared, ...byTier.other].filter((id) => {
      const m = manifest.get(id) ?? '';
      return m === 'aws_sqs';
    });
    for (const queue of queues) {
      const readers = [...(incoming.get(queue) ?? []), ...(outgoing.get(queue) ?? [])].filter(
        (p) => classifyInfraTier(manifest.get(p) ?? '') === 'compute',
      );
      if (readers.length === 0) continue;
      out.push({
        patternId: 'queue-worker',
        label: 'Queue workers',
        memberIds: [queue, ...new Set(readers)],
        anchorId: queue,
        hints: { backgroundColor: '#fef3c7' },
      });
    }
    return out;
  }
}
